import type { ServerResponse } from './types.ServerResponse'

export type AccessKeyType = {
  code: string
  expire: string | number | Date // "2024-03-11T21:20:14.201Z"
  roles: string[] //'editor'
}
/**
 * Client-side definition of an attribute type.
 * This is used to define the structure of attributes in the dashboard.
 * It includes properties such as target, type, key, main, meta, method, and text.
 */
export interface AttributeType {
  key: string
  target: 'attribute' | 'meta' | 'data'
  type: 'number' | 'percent' | 'other' | 'tags'
  meta: boolean
  method: ServerResponse.MetricsMethodType
  text: string
  nativeKey?: string
}

export type TargetType = 'attribute' | 'entity' | 'meta'

export type MetricsType = {
  uid: string
  values: null | string[]
  method: null | string
  show: null | boolean
  disabled?: string[]
}

export type SectionType = {
  name: string
  target: TargetType
  items: string[]
  collapsed: boolean
  metrics: MetricsType[]
}

export type SectionAttribute = {
  target: TargetType
  name: string
  attribute: string
}

export type Metrics = {
  id: string
  name: string
  short: string
  type: string
  value?: string | string[] | number
}

export type MetricsValue = {
  type: string
  value: string | string[] | number
}

export type ItemType = Partial<ServerResponse.InsightsSchema.SubjectType> & {
  letter?: string
  pinned?: boolean
  uid: string
  waveUid?: string
  new?: boolean
}

export type ItemsType = ItemType[]

export type CaptionType = {
  target: 'attribute' | 'entity' | 'meta'
  type: string
  section: string
  attribute: string
  collapsed: boolean
  metrics: Metrics
  metricsOptions: { id: string; name: string }[]
  defaultMetrics: Metrics
  selectedMetrics?: string[]
  showMetrics?: boolean
}

export interface ColorModel {
  background: null | string
  text: null | string
}

export interface ColorsModel {
  upper: null | ColorModel
  lower: null | ColorModel
  toJS: () => ColorsModel
  getIn: <T>(path: Array<string>) => T | null
}
export interface MilestoneModel {
  uid: string
  title: string
  date: string
}
export interface TimeZoneModel {
  title: string
  offset: string
}

export type DataItem = ServerResponse.InsightsDataItems.DataItem
export type DataItemValue = ServerResponse.InsightsDataItems.DataItemValue
export type DataItemAttribute = ServerResponse.InsightsDataItems.DataItemAttribute
export type Data = ServerResponse.InsightsDataItems.Data
export type StatItem = ServerResponse.InsightsStatItems.StatItem
export type StatItemAttribute = ServerResponse.InsightsStatItems.StatItemAttribute
export type Stat = ServerResponse.InsightsStatItems.Stat
export type SigTestItem = ServerResponse.InsightsStatItems.SigTestItem
export type SurveyItemValue = ServerResponse.InsightsDataItems.SurveyItemValue
export type AggregatedValues = { total: number | null; values: Record<string, DataItemAttribute> }

// TODO: Compare with ServerResponse.InsightsSchema.SubjectType
export type SubjectType = {
  uid: string
  type: string
  text: string
  nativeUid?: string
  waveUid?: string
  waveIndex?: number
  from?: string
  to?: string
  split?: {
    id: string | number
    index: number
    text: string
  }
  wave?: {
    id: string | number
    index: number
    text: string
  }
  image?: object
  aggregatedValues?: AggregatedValues
}

export type SubjectsType = Record<string, SubjectType>
export type StatItemValue = ServerResponse.InsightsStatItems.StatItemValue

export type ItemsArrayType = ItemType[]
export type ItemHashType = string
export type ItemsHashesChunksType = ItemHashType[][]

export const SendStatus = {
  Send: 0,
  Sending: 1,
  Sent: 2,
  SentWithError: 3
} as const

export type SendStatusType = (typeof SendStatus)[keyof typeof SendStatus]

export type FilterOptionsType = {
  trait: Array<{
    itId: number
    traitId: string // 'gender'
    id: string // 'trait:gender'
    title: string // 'Gender'
    type: string // 't'
    geo: boolean
    dynamic: boolean
    native: boolean
    order: number
    rule: unknown // TBD: null |
    deleted: boolean
    options: Array<{
      label: string
      value: string
      group: boolean
    }>
  }>
  variable: Array<{
    id: string // "variable:Source",
    title: string // "Source",
    options: Array<{
      label: string //  "Xpert solution",
      value: string // "Xpert solution"
    }>
  }>
  [key: string]: Array<{
    id: string // "variable:Source",
    title: string // "Source",
  }>
}

export type BenchMarkType = {
  uid: unknown
  type: unknown
  title: unknown
  description: string
  values: object
  image: unknown
}

export type EditorType = {
  itemIndex: number
  type: 'edit' | 'change' | 'rename' | 'replace' | 'add' | null
}

export type AttributeRow = {
  type: 'attribute' | 'metrics'
  section: string
  target: TargetType
  attribute: string
  metrics: Metrics | null
  metricsOptions: Metrics[] | null
  defaultMetrics: Metrics | null
  showMetrics: boolean
  selectedMetrics: string[]
  disabled: string[]
  name?: string
}

export type SectionItemType = {
  type: 'section'
  section: string
  target: TargetType
  collapsed: boolean
  name?: string
}

export type AttributeItemType = {
  type: 'attribute'
  section: string
  target: TargetType
  attribute: string
  name?: string
}

export type AttributeCaption = AttributeItemType | SectionItemType | AttributeRow

export const UpdateBlocksAction = {
  Replace: 0,
  Delete: 1,
  Add: 2
} as const

export type UpdateBlocksActionType = (typeof UpdateBlocksAction)[keyof typeof UpdateBlocksAction]

export type TrackTargetType = 'attribute' | 'entity'

export type TrackAttributesModel = {
  target: TrackTargetType
  attribute?: string
  entity?: string
}

export type TrackTargetValueType = {
  track: string
  caption: string
  target: TrackTargetType
}
export const defaultTrackTargets = {
  attribute: { track: 'Track an attribute', caption: 'entities', target: 'entity' },
  entity: { track: 'Track an entity', caption: 'attributes', target: 'attribute' }
} as Record<TrackTargetType, TrackTargetValueType>

export type PresetEntryType = Record<string, string[]>
export type PresetsType = Record<string, PresetEntryType>

export type WaveIntervalsType = ServerResponse.InsightsSchema.WaveIntervalsType

export type SplitType = ServerResponse.InsightsSchema.SplitType

export type SplitOption = {
  id: string | number
  index: number
  text: string
}

// eslint-disable-next-line immutablejs/no-native-map-set
export type UIDSList = Set<string>

export type SubjectEditorTypes = 'edit' | 'change' | 'rename' | 'replace' | 'delete' | 'add'

export type SubjectEditorActions = 'save' | 'change' | 'apply'

export type SubjectEditorStates = 'updating' | 'uploading' | null

export type SubjectEditorValue = {
  value: unknown
  method: string
  type: string
  metrics?: Record<string, MetricsValue>
}

export type SubjectEditorData = {
  uid: string
  type: string
  text: string
  waveIndex: number
  nativeUid?: string
  hash?: string
  image?: object
  values?: Record<string, SubjectEditorValue>
}

export type EditorError = {
  type: string
  message?: string[]
}

export type SubjectEditor = {
  type: SubjectEditorTypes
  action?: SubjectEditorActions
  itemIndex: number
  menuIndex: number
  changed?: boolean
  scroll: boolean
  data?: SubjectEditorData
  state?: SubjectEditorStates
  errors?: Record<string, string[]>
}
